export type MarketFamily =
  | "result_1x2"
  | "total_goals"
  | "both_teams_to_score"
  | "handicap"
  | "total_corners"
  | "total_cards"
  | "total_shots"
  | "total_shots_on_target";

/** Filtros compartilhados entre Odds de Valor e a busca de oportunidades no Chat. */
export interface ValueOddsFilters {
  minimumAdvantagePercent: number;
  minimumHitRatePercent: number | null;
  minimumProbabilityPercent: number | null;
  minimumOdds: number | null;
  maximumOdds: number | null;
  marketFamily: MarketFamily | null;
  search: string;
  dateFrom: string | null;
  dateTo: string | null;
}

export type MarketPeriod = "FT90" | "HT" | "FT" | "FTP" | "ATP";

export type SettlementRule =
  | "no_extra_time"
  | "extra_time_included"
  | "penalties_excluded";

export type Selection =
  | "home"
  | "away"
  | "draw"
  | "over"
  | "under"
  | "yes"
  | "no";

export interface MarketKey {
  family: MarketFamily;
  period: MarketPeriod;
  subjectId: string;
  selection: Selection;
  line?: number;
  settlementRule: SettlementRule;
}

export interface Quote {
  fixtureId: string;
  bookmakerId: string;
  bookmakerName: string;
  /** Indica a procedência da cotação; opcional para compatibilidade com snapshots antigos. */
  source?: "manual" | "consensus" | "bookmaker";
  /** Provedor que forneceu o preço; ausente em cotações manuais antigas. */
  provider?: "goaldir" | "api-football";
  marketKey: MarketKey;
  decimalOdds: number;
  observedAt: string;
  expiresAt: string;
}

export type CanonicalFixtureStatus =
  | "scheduled"
  | "live"
  | "finished"
  | "postponed"
  | "cancelled"
  | "unresolved"
  | "abandoned"
  | "awarded";

export interface TeamStatsSnapshot {
  teamExternalId: string;
  period: MarketPeriod;
  goals: number | null;
  shots: number | null;
  shotsOnTarget: number | null;
  corners: number | null;
  yellowCards: number | null;
  redCards: number | null;
  fouls: number | null;
  offsides: number | null;
  possession: number | null;
  passes: number | null;
  xg: number | null;
  xgAgainst: number | null;
}

export interface PlayerStatsSnapshot {
  playerExternalId: string | null;
  playerExternalName: string | null;
  teamExternalId: string | null;
  minutes: number | null;
  goals: number | null;
  assists: number | null;
  shots: number | null;
  shotsOnTarget: number | null;
  yellowCards: number | null;
  redCards: number | null;
}

export interface LineupEntrySnapshot {
  playerExternalId: string | null;
  name: string;
  position: string | null;
  shirtNumber: string | null;
  isStarter: boolean;
  sortOrder: number;
}

export interface LineupSnapshot {
  teamExternalId: string;
  state: "confirmed" | "predicted";
  formation: string | null;
  entries: LineupEntrySnapshot[];
}

export interface FixtureTeamStatsPair {
  home: TeamStatsSnapshot | null;
  away: TeamStatsSnapshot | null;
}

export interface FixtureLineupsPair {
  home: LineupSnapshot | null;
  away: LineupSnapshot | null;
}

export interface Fixture {
  id: string;
  provider: string;
  providerId: string;
  competitionId: string;
  competitionName: string;
  season: string;
  /** Rótulo humano da temporada (ex.: "LaLiga 2 26/27"); `season` guarda o ano do recorte. */
  seasonLabel?: string;
  homeTeamId: string;
  homeTeamName: string;
  awayTeamId: string;
  awayTeamName: string;
  kickoffAt: string;
  status: CanonicalFixtureStatus;
  venue?: string;
  referee?: string;
}

export interface Team {
  id: string;
  provider: string;
  providerId: string;
  name: string;
  shortName?: string;
  country?: string;
}

export interface Competition {
  id: string;
  provider: string;
  providerId: string;
  name: string;
  country?: string;
  tier?: number;
}

export interface TeamHistorySample {
  fixtureId: string;
  date: string;
  isHome: boolean;
  /** Identificação da partida usada para comprovar uma frequência histórica. */
  homeTeamName?: string;
  awayTeamName?: string;
  goalsFor: number | null;
  goalsAgainst: number | null;
  shots: number | null;
  shotsOnTarget: number | null;
  corners: number | null;
  yellowCards: number | null;
  redCards: number | null;
  possession: number | null;
  passes: number | null;
  xGFor: number | null;
  xGAgainst: number | null;
  /** T05 — valores permitidos (do adversário na mesma partida), quando disponíveis. */
  shotsAgainst?: number | null;
  shotsOnTargetAgainst?: number | null;
  cornersAgainst?: number | null;
  yellowCardsAgainst?: number | null;
  redCardsAgainst?: number | null;
}

export interface DescriptiveStats {
  selectedCount: number;
  validCount: number;
  missingCount: number;
  coverage: number | null;
  mean: number | null;
  median: number | null;
  sampleStdDev: number | null;
  hits: number | null;
  hitRate: number | null;
}

export interface Convergence {
  marketKey: MarketKey;
  homeFrequency: number;
  awayFrequency: number;
  level: "high" | "moderate" | "low";
  homeSample: { selected: number; valid: number };
  awaySample: { selected: number; valid: number };
}

/**
 * T05 (§6) — cruzamento produzido × permitido, no recorte casa/fora.
 * Produzido pelo mandante contra o que o visitante permite; e vice-versa.
 * Todos os valores vêm de DescriptiveStats do engine (nada é inferido pela UI).
 */
export interface ProducedAllowedRow {
  key: string;
  label: string;
  /** Métrica do mandante produzindo (ex.: goalsFor) e do visitante permitindo (ex.: goalsAgainst). */
  homeProduced: DescriptiveStats;
  awayAllowed: DescriptiveStats;
  /** Métrica do visitante produzindo e do mandante permitindo. */
  awayProduced: DescriptiveStats;
  homeAllowed: DescriptiveStats;
  /** Unidade de exibição (ex.: "gols", "%"). */
  unit: string;
}

export interface PoissonModel {
  type: "poisson-independent-v0.1";
  lambdaHome: number;
  lambdaAway: number;
  residualMass: number;
  maxGoals: number;
  matrix: number[][];
  limitations: string[];
}

export interface MarketProbability {
  marketKey: MarketKey;
  status: "experimental_value" | "no_value" | "awaiting_odds" | "insufficient_data" | "unsupported_market";
  pWin: number | null;
  pPush: number | null;
  pLoss: number | null;
  fairOdds: number | null;
  ev: number | null;
  reason: string;
}

export interface AnalysisSnapshot {
  schemaVersion: number;
  /** Versão do snapshot dentro do workspace+fixture (1, 2, ...). Reanálise cria nova versão. */
  snapshotVersion?: number;
  engineVersion: string;
  dataMode: "demo" | "live";
  fixture: Fixture;
  cutoffAt: string;
  sourceMetadata: Array<{
    provider: string;
    endpoint: string;
    collectedAt: string;
    expiresAt: string;
    normalizerVersion: string;
  }>;
  samples: {
    general: { home: TeamHistorySample[]; away: TeamHistorySample[] };
    venue: { home: TeamHistorySample[]; away: TeamHistorySample[] };
    season: { home: TeamHistorySample[]; away: TeamHistorySample[] };
  };
  qualityByMetric: Record<string, { home: DescriptiveStats; away: DescriptiveStats }>;
  descriptiveStats: Record<string, { home: DescriptiveStats; away: DescriptiveStats }>;
  convergence: Convergence[];
  /** T05 (§6) — cruzamento produzido × permitido no recorte casa/fora. */
  producedAllowed?: ProducedAllowedRow[];
  model: PoissonModel | null;
  markets: MarketProbability[];
  quotes: Quote[];
  narrative: {
    text: string;
    provider: string | null;
    model: string | null;
    promptVersion: string | null;
    fallback: boolean;
  };
  generatedAt: string;
  /** T05 — metadados de proveniência/reprodutibilidade do snapshot. */
  normalizerVersions?: Record<string, string>;
  usedFixtureIds?: string[];
  sampleSizes?: Record<string, { home: number; away: number }>;
  coverage?: Record<string, { home: number | null; away: number | null }>;
  warnings?: string[];
  insufficient?: Array<{ scope: string; reason: string }>;
  dataset?: {
    general: string[];
    venue: string[];
    season: string[];
    union: string[];
  };
  providerCalls?: {
    cacheHits: number;
    cacheMisses: number;
    externalCalls: number;
    byProvider: Record<string, number>;
  };
}

// ============================================================
// T05 — contrato do serviço central (AnalysisService) exposto ao web/Telegram.
// Somente tipos (seguro para o bundle do navegador; sem dependência de provider).
// ============================================================

export interface FixtureSearchRequest {
  query?: string;
  fromUtc: string;
  toUtc: string;
  competitionId?: string;
  /** Maximum number of fixtures to collect across provider pages (default: one page). */
  limit?: number;
}

export interface FixtureSearchResponse {
  fixtures: Fixture[];
  missingReasons: string[];
}

export interface CreateAnalysisRequest {
  fixtureId: string;
  /** Corte explícito (≤ agora e ≤ kickoff). Ausente = min(agora, kickoff). */
  cutoffAt?: string;
}

export interface CreateAnalysisResponse {
  analysisId: string;
  snapshotVersion: number;
  status: "completed" | "failed" | "queued";
  reused: boolean;
  snapshot: AnalysisSnapshot | null;
  errorCode?: string | null;
}

export interface GetAnalysisRequest {
  analysisId: string;
}

export interface AnalysisListItem {
  id: string;
  fixtureId: string;
  homeTeamName: string;
  awayTeamName: string;
  competitionName: string;
  kickoffAt: string;
  generatedAt: string | null;
  status: string;
  engineVersion: string;
  snapshotVersion: number;
  dataMode: "demo" | "live";
  hasModel: boolean;
}

export interface AnalysisHistoryResponse {
  items: AnalysisListItem[];
}

export interface AnalysisJob {
  id: string;
  userId: string;
  fixtureId: string;
  idempotencyKey: string;
  status: "queued" | "collecting" | "calculating" | "explaining" | "completed" | "failed";
  step: string;
  attempt: number;
  leaseUntil: string | null;
  createdAt: string;
  updatedAt: string;
  error?: string;
}

export interface Bookmaker {
  id: string;
  userId: string;
  name: string;
  slot: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserProfile {
  userId: string;
  timezone: string;
  language: string;
  telegramLinked: boolean;
  telegramChatId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderResult<T> {
  status: "ok" | "partial" | "unavailable" | "error";
  data: T | null;
  coverage: {
    provider: string;
    endpoint: string;
    collectedAt: string;
    expiresAt: string;
    normalizerVersion: string;
    missingReasons: string[];
  };
  nextCursor?: string;
}

export interface FootballProvider {
  getMatches(params: {
    query?: string;
    fromUtc: string;
    toUtc: string;
    competitionId?: string;
    cursor?: string;
  }): Promise<ProviderResult<Fixture[]>>;
  getCoverage(params: {
    competitionId: string;
    season: string;
    matchId?: string;
  }): Promise<ProviderResult<Record<string, boolean>>>;
  getTeamHistory(params: {
    teamId: string;
    cutoffAt: string;
    condition: "home" | "away" | "all";
    cursor?: string;
  }): Promise<ProviderResult<TeamHistorySample[]>>;
  getMatchStats(params: {
    matchId: string;
    period: MarketPeriod;
  }): Promise<ProviderResult<FixtureTeamStatsPair>>;
  getPlayerStats(params: {
    matchId: string;
    playerId?: string;
  }): Promise<ProviderResult<PlayerStatsSnapshot[]>>;
  getLineup(params: { matchId: string }): Promise<ProviderResult<FixtureLineupsPair>>;
  getInjuries(params: { matchId: string } | { teamId: string; referenceAt: string }): Promise<ProviderResult<Record<string, unknown>[]>>;
  getIncidents(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown>[]>>;
  getH2H(params: { teamIds: string[]; cutoffAt: string; cursor?: string }): Promise<ProviderResult<Fixture[]>>;
  getReferee(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown> | null>>;
  getConsensusOdds(params: { matchId: string }): Promise<ProviderResult<Quote[]>>;
  /** Cotações pré-jogo por casa, sem misturar com consenso de mercado. */
  getBookmakerOdds(params: { fixture: Fixture }): Promise<ProviderResult<Quote[]>>;
  getPredictions(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown>[]>>;
  getShotMap(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown>>>;
  getMomentum(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown>>>;
  getAveragePositions(params: { matchId: string }): Promise<ProviderResult<Record<string, unknown>>>;
}

export interface StatisticalEngine {
  analyze(input: {
    fixture: Fixture;
    cutoffAt: string;
    samples: {
      general: { home: TeamHistorySample[]; away: TeamHistorySample[] };
      venue: { home: TeamHistorySample[]; away: TeamHistorySample[] };
      season: { home: TeamHistorySample[]; away: TeamHistorySample[] };
    };
    engineVersion: string;
  }): Promise<{
    qualityByMetric: Record<string, { home: DescriptiveStats; away: DescriptiveStats }>;
    descriptiveStats: Record<string, { home: DescriptiveStats; away: DescriptiveStats }>;
    convergence: Convergence[];
    model: PoissonModel | null;
    markets: MarketProbability[];
  }>;
}

export interface OddsComparator {
  compare(input: {
    marketKey: MarketKey;
    quotes: Quote[];
    outcomeProbabilities: { pWin: number; pPush: number; pLoss: number };
  }): {
    bestQuote: Quote | null;
    tiedQuotes: Quote[];
    fairOdds: number | null;
    ev: number | null;
    status: "experimental_value" | "no_value" | "awaiting_odds";
  };
}

export interface NarrativeProvider {
  explain(snapshot: AnalysisSnapshot): Promise<{
    text: string;
    usage: { promptTokens: number; completionTokens: number };
  }>;
}

export interface AgentService {
  requestAnalysis(input: {
    actor: "web" | "telegram";
    userId: string;
    fixtureId: string;
    idempotencyKey: string;
  }): Promise<{ analysisId: string; status: string }>;
  getAnalysis(userId: string, analysisId: string): Promise<AnalysisSnapshot | null>;
  listAnalyses(userId: string, cursor?: string, limit?: number): Promise<{
    items: Array<{ id: string; fixtureId: string; status: string; generatedAt: string }>;
    nextCursor?: string;
  }>;
  addQuote(input: {
    userId: string;
    analysisId: string;
    quote: Quote;
    idempotencyKey: string;
  }): Promise<{ revisionId: string }>;
  addMessage(input: {
    userId: string;
    analysisId: string;
    text: string;
    clientMessageId: string;
  }): Promise<{ messageId: string; reply: string }>;
}

export const DEMO_MODE_KEY = "tracker-bet-analystic.demo" as const;
export const DEMO_USER_ID = "demo-user-001" as const;
export const MAX_BOOKMAKERS = 5 as const;

export function formatDecimal(value: number): string {
  return value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function parseDecimal(input: string): number | null {
  const normalized = input.replace(",", ".");
  const value = Number(normalized);
  return Number.isFinite(value) && value > 1 ? value : null;
}

export function formatDateBR(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

export function formatDateTimeBR(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });
}

export function marketKeyToString(key: MarketKey): string {
  const parts = [
    key.family,
    key.period,
    key.subjectId,
    key.selection,
    key.line?.toString() ?? "",
    key.settlementRule,
  ];
  return parts.filter(Boolean).join("|");
}

export function marketKeyFromString(str: string): MarketKey | null {
  const parts = str.split("|");
  if (parts.length < 5) return null;
  const subjectId = parts[2];
  if (!subjectId) return null;
  return {
    family: parts[0] as MarketFamily,
    period: parts[1] as MarketPeriod,
    subjectId,
    selection: parts[3] as Selection,
    line: parts[4] ? Number(parts[4]) : undefined,
    settlementRule: (parts[5] ?? "no_extra_time") as SettlementRule,
  };
}
